/**
 * features/finance/pettyWrite.ts
 * ------------------------------------------------------------------
 * Writing petty cash.
 *
 * A SLIP IS NEVER EDITED AND NEVER DELETED. It is somebody's statement
 * that this much cash left the tin on this day, and the tin has already
 * been emptied by that much. Correcting one by editing it would make the
 * records agree with a story rather than with the money; a correction is
 * a REVERSING slip, which leaves both visible and keeps the running
 * balance honest at every point in between.
 *
 * THE OVER-SPEND CHECK IS RE-RUN HERE, not just on the screen. The tin
 * cannot go negative in reality, and a slip that takes it below zero
 * means the amount is wrong or cash arrived from somewhere unrecorded —
 * a question to answer before the entry exists, not after.
 *
 * A COUNT VARIANCE IS RECORDED, NEVER SILENTLY CORRECTED. A recurring
 * small shortage is a pattern worth seeing; a corrected one is invisible.
 * ------------------------------------------------------------------ */
import { collection, doc, runTransaction } from 'firebase/firestore';
import { db, setDoc, updateDoc } from '@/lib/firebase';
import {
  PETTY_COLLECTIONS, pettySlipCounterId, formatPettySlipNo,
  floatState, canSpend, reconcileCount,
  type PettyFloat, type PettySlip, type PettyCount,
} from '@/lib/pettyCash';

const now = () => new Date().toISOString();

export interface NewFloatInput {
  name: string;
  floatAmount: number;
  custodianId: string;
  custodianName: string;
  accountCode: string;
  notes?: string;
  actor: { uid: string; name?: string };
}

/** Open a tin. A float with no named custodian is nobody's responsibility. */
export async function createFloat(input: NewFloatInput): Promise<PettyFloat> {
  if (!input.name.trim()) throw new Error('Give the float a name.');
  if (!(Number(input.floatAmount) > 0)) throw new Error('The float has to be more than nil.');
  if (!input.custodianId) throw new Error('Name who is responsible for this tin.');

  const at = now();
  const ref = doc(collection(db, PETTY_COLLECTIONS.floats));
  const float: PettyFloat = {
    id: ref.id,
    name: input.name.trim(),
    floatAmount: Math.round(Number(input.floatAmount) * 100) / 100,
    custodianId: input.custodianId,
    custodianName: input.custodianName,
    accountCode: input.accountCode,
    active: true,
    openedAt: at.slice(0, 10),
    ...(input.notes?.trim() ? { notes: input.notes.trim() } : {}),
    createdBy: input.actor.uid,
    createdAt: at,
    updatedAt: at,
  };
  await setDoc(ref, float);
  return float;
}

export interface NewSlipInput {
  float: PettyFloat;
  slips: PettySlip[];
  counts: Pick<PettyCount, 'variance'>[];
  date: string;
  description: string;
  amount: number;
  accountCode: string;
  paidTo: string;
  hasReceipt: boolean;
  actor: { uid: string; name?: string };
}

export async function recordSlip(input: NewSlipInput): Promise<PettySlip> {
  if (!input.description.trim()) throw new Error('Say what the money was spent on.');
  if (!input.accountCode) throw new Error('Code the slip — an uncoded slip is a hole somebody has to guess at later.');

  const state = floatState(input.float, input.slips, input.counts);
  const verdict = canSpend(state, input.amount);
  if (!verdict.ok) throw new Error(verdict.reason ?? 'That cannot be paid from this tin.');

  const year = input.date.slice(0, 4);
  const at = now();

  return runTransaction(db, async (tx) => {
    const counterRef = doc(db, PETTY_COLLECTIONS.counters, pettySlipCounterId(year));
    const snap = await tx.get(counterRef);
    const seq = ((snap.exists() ? Number(snap.data().seq) : 0) || 0) + 1;
    tx.set(counterRef, { seq, updatedAt: at }, { merge: true });

    const ref = doc(collection(db, PETTY_COLLECTIONS.slips));
    const slip: PettySlip = {
      id: ref.id,
      slipNo: formatPettySlipNo(seq, year),
      floatId: input.float.id,
      date: input.date,
      description: input.description.trim(),
      amount: Math.round(Number(input.amount) * 100) / 100,
      accountCode: input.accountCode,
      paidTo: input.paidTo.trim(),
      hasReceipt: input.hasReceipt,
      fy: `FY${year}`,
      recordedBy: input.actor.uid,
      ...(input.actor.name ? { recordedByName: input.actor.name } : {}),
      createdAt: at,
    };
    tx.set(ref, slip);
    return slip;
  });
}

/**
 * Correct a slip by reversing it.
 *
 * Two writes, and both matter: the original is stamped with what reversed
 * it, and a mirror slip carries the opposite amount. Either alone would
 * leave the tin's history disagreeing with its balance.
 */
export async function reverseSlip(
  slip: PettySlip, reason: string, actor: { uid: string; name?: string },
): Promise<void> {
  if (slip.reversedBy) throw new Error('That slip has already been reversed.');
  if (slip.reimbursementId) {
    throw new Error('That slip has been reimbursed. Reverse it with a new slip against the current float.');
  }

  const at = now();
  const year = slip.date.slice(0, 4);

  await runTransaction(db, async (tx) => {
    const counterRef = doc(db, PETTY_COLLECTIONS.counters, pettySlipCounterId(year));
    const snap = await tx.get(counterRef);
    const seq = ((snap.exists() ? Number(snap.data().seq) : 0) || 0) + 1;
    tx.set(counterRef, { seq, updatedAt: at }, { merge: true });

    const ref = doc(collection(db, PETTY_COLLECTIONS.slips));
    tx.set(ref, {
      id: ref.id,
      slipNo: formatPettySlipNo(seq, year),
      floatId: slip.floatId,
      date: at.slice(0, 10),
      description: `Reverses ${slip.slipNo}: ${reason.trim()}`,
      amount: -slip.amount,
      accountCode: slip.accountCode,
      paidTo: slip.paidTo,
      hasReceipt: slip.hasReceipt,
      fy: `FY${year}`,
      reversesId: slip.id,
      recordedBy: actor.uid,
      ...(actor.name ? { recordedByName: actor.name } : {}),
      createdAt: at,
    } satisfies PettySlip);

    tx.update(doc(db, PETTY_COLLECTIONS.slips, slip.id), { reversedBy: ref.id });
  });
}

/**
 * Restore the float by reimbursing exactly what was spent.
 *
 * Stamps every unreimbursed slip with the claim, which is what takes them
 * out of the next claim. Batched so a partly-stamped reimbursement cannot
 * leave some slips claimable twice.
 */
export async function reimburse(
  floatId: string, slips: PettySlip[], actor: { uid: string; name?: string },
): Promise<{ claimId: string; total: number }> {
  const live = slips.filter((s) => !s.reimbursementId && !s.reversedBy && !s.reversesId);
  if (live.length === 0) throw new Error('There is nothing to claim back.');

  const at = now();
  const claimId = doc(collection(db, PETTY_COLLECTIONS.slips)).id;
  const total = Math.round(live.reduce((n, s) => n + s.amount, 0) * 100) / 100;

  await runTransaction(db, async (tx) => {
    for (const s of live) {
      tx.update(doc(db, PETTY_COLLECTIONS.slips, s.id), {
        reimbursementId: claimId,
        reimbursedAt: at,
        reimbursedBy: actor.uid,
      });
    }
  });

  void floatId;
  return { claimId, total };
}

export interface NewCountInput {
  float: PettyFloat;
  slips: PettySlip[];
  counts: PettyCount[];
  countedAmount: number;
  date: string;
  explanation?: string;
  actor: { uid: string; name?: string };
}

/** Record a physical count. The variance is stored, whatever it is. */
export async function recordCount(input: NewCountInput): Promise<PettyCount> {
  const state = floatState(input.float, input.slips, input.counts);
  const result = reconcileCount(state, input.countedAmount);

  if (!result.balanced && !input.explanation?.trim()) {
    throw new Error(`${result.summary} Say what you think happened before recording it.`);
  }

  const at = now();
  const ref = doc(collection(db, PETTY_COLLECTIONS.counts));
  const count: PettyCount = {
    id: ref.id,
    floatId: input.float.id,
    date: input.date,
    countedAmount: result.countedAmount,
    expectedAmount: result.expectedAmount,
    variance: result.variance,
    ...(input.explanation?.trim() ? { explanation: input.explanation.trim() } : {}),
    countedBy: input.actor.uid,
    ...(input.actor.name ? { countedByName: input.actor.name } : {}),
    createdAt: at,
  };
  await setDoc(ref, count);
  return count;
}

export async function closeFloat(float: PettyFloat): Promise<void> {
  await updateDoc(doc(db, PETTY_COLLECTIONS.floats, float.id), {
    active: false, updatedAt: now(),
  });
}
